import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { getApi, postApi } from "../../services/api/globalApi";
import { utilsState } from "../../sotre/redux/features/utilsSlice";
import { errorMessage, successMessage } from "../../utils/toastify-message/ToastifyMessage";
import AdminFormLayout from "../../components/common/form-layout/AdminFormLayout";

const MODES = [
  { value: "all", label: "شارژ همگانی" },
  { value: "roles", label: "بر اساس نقش" },
  { value: "users", label: "کاربران انتخابی" },
  { value: "exceptUsers", label: "همهٔ کاربران به‌جز افراد انتخاب‌شده" },
];

export default function BulkDeposit() {
  const navigate = useNavigate();
  const { utils } = useSelector(utilsState);
  const [mode, setMode] = useState("");
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batchId, setBatchId] = useState(null);

  const roleOptions = Object.values(utils?.ROLES || {}).filter((role) => role?.value);
  const selection = {
    mode,
    ...(mode === "roles" ? { roles } : {}),
    ...(["users", "exceptUsers"].includes(mode) ? { userIds: users.map((user) => user._id) } : {}),
  };

  useEffect(() => {
    if (!["users", "exceptUsers"].includes(mode)) return;
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const response = await getApi({ path: "admin/users", params: { page: 1, limit: 20, searchText: search } });
        if (active) setSearchResults(response?.data?.result?.data || []);
      } catch (error) {
        if (active) errorMessage(error?.response?.data?.errors?.[0]?.message || "دریافت کاربران ناموفق بود");
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [mode, search]);

  const changeMode = (value) => {
    setMode(mode === value ? "" : value);
    setRoles([]);
    setUsers([]);
    setPreview(null);
    setBatchId(null);
  };

  const validationError = () => {
    if (!mode) return "نوع شارژ را انتخاب کنید";
    if (mode === "roles" && !roles.length) return "حداقل یک نقش انتخاب کنید";
    if (["users", "exceptUsers"].includes(mode) && !users.length) return "حداقل یک کاربر انتخاب کنید";
    if (!/^\d+$/.test(amount) || Number(amount) < 1 || !Number.isSafeInteger(Number(amount))) return "مبلغ باید عدد صحیح مثبت باشد";
    if (!description.trim()) return "توضیحات را وارد کنید";
    return null;
  };

  const showPreview = async (event) => {
    event.preventDefault();
    const error = validationError();
    if (error) return errorMessage(error);
    setBusy(true);
    try {
      const response = await postApi({ path: "admin/wallets/deposit/bulk/preview", body: JSON.stringify(selection) });
      const count = response?.data?.result?.count;
      if (!count) return errorMessage("کاربری برای شارژ یافت نشد");
      setPreview({ count, amount: Number(amount), description: description.trim(), selection });
      setBatchId(crypto.randomUUID());
    } catch (error) {
      errorMessage(error?.response?.data?.errors?.[0]?.message || "پیش‌نمایش شارژ ناموفق بود");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!preview || busy) return;
    setBusy(true);
    try {
      const response = await postApi({
        path: "admin/wallets/deposit/bulk",
        body: JSON.stringify({ ...preview.selection, amount: preview.amount, description: preview.description, batchId }),
      });
      successMessage(`کیف پول ${response?.data?.result?.count} کاربر شارژ شد`);
      navigate("/wallets/bulk-history");
    } catch (error) {
      errorMessage(error?.response?.data?.errors?.[0]?.message || "شارژ کیف پول ناموفق بود؛ می‌توانید دوباره تلاش کنید");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminFormLayout title="شارژ گروهی کیف پول" description="به هر کاربر انتخاب‌شده مبلغ یکسانی واریز می‌شود.">
      <form onSubmit={showPreview} className="space-y-6">
        <fieldset className="space-y-3">
          <legend className="mb-2 font-medium">گروه گیرندگان</legend>
          {MODES.map((item) => (
            <label key={item.value} className={`flex items-center gap-3 ${mode && mode !== item.value ? "opacity-50" : ""}`}>
              <input type="checkbox" checked={mode === item.value} disabled={Boolean(mode && mode !== item.value) || busy} onChange={() => changeMode(item.value)} />
              {item.label}
            </label>
          ))}
        </fieldset>

        {mode === "roles" && (
          <fieldset className="space-y-2">
            <legend className="font-medium">نقش‌ها</legend>
            <div className="flex flex-wrap gap-4">
              {roleOptions.map((role) => (
                <label key={role.value} className="flex items-center gap-2">
                  <input type="checkbox" checked={roles.includes(role.value)} disabled={busy} onChange={() => {
                    setRoles((current) => current.includes(role.value) ? current.filter((value) => value !== role.value) : [...current, role.value]);
                    setPreview(null); setBatchId(null);
                  }} />
                  {role.name}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {["users", "exceptUsers"].includes(mode) && (
          <div className="space-y-3">
            <label htmlFor="bulk-user-search" className="block font-medium">{mode === "exceptUsers" ? "افراد مستثنا از شارژ" : "انتخاب کاربران"}</label>
            {mode === "exceptUsers" && <p className="text-sm text-amber-700 dark:text-amber-300">کیف پول افراد انتخاب‌شده شارژ نمی‌شود؛ مبلغ به سایر کاربران واریز خواهد شد.</p>}
            <input id="bulk-user-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="نام یا شماره موبایل" className="w-full rounded-lg border p-2.5 dark:bg-gray-700" />
            <div className="max-h-48 overflow-y-auto rounded-lg border p-2">
              {searchResults.map((user) => (
                <label key={user._id} className="flex items-center gap-2 p-2">
                  <input type="checkbox" checked={users.some((selected) => selected._id === user._id)} disabled={busy} onChange={() => {
                    setUsers((current) => current.some((selected) => selected._id === user._id) ? current.filter((selected) => selected._id !== user._id) : [...current, user]);
                    setPreview(null); setBatchId(null);
                  }} />
                  {user.name || "بدون نام"} — {user.mobile}
                </label>
              ))}
            </div>
            <p>تعداد انتخاب‌شده: {users.length}</p>
            {users.length > 0 && <div className="flex flex-wrap gap-2">{users.map((user) => (
              <span key={user._id} className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-2 py-1 dark:bg-gray-700">
                {user.name || user.mobile}
                <button type="button" disabled={busy} aria-label={`حذف ${user.name || user.mobile}`}
                  onClick={() => { setUsers((current) => current.filter((selected) => selected._id !== user._id)); setPreview(null); setBatchId(null); }}
                  className="text-red-600 disabled:opacity-50">×</button>
              </span>
            ))}</div>}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">مبلغ برای هر نفر (تومان)
            <input type="number" min="1" step="1" value={amount} disabled={busy} onChange={(event) => { setAmount(event.target.value); setPreview(null); setBatchId(null); }} className="block w-full rounded-lg border p-2.5 dark:bg-gray-700" />
          </label>
          <label className="space-y-2">توضیحات
            <input type="text" value={description} disabled={busy} onChange={(event) => { setDescription(event.target.value); setPreview(null); setBatchId(null); }} className="block w-full rounded-lg border p-2.5 dark:bg-gray-700" />
          </label>
        </div>
        <button type="submit" disabled={busy} className="rounded-lg bg-blue-700 px-5 py-2.5 text-white disabled:opacity-50">بررسی تعداد گیرندگان</button>
      </form>

      {preview && <div className="mt-6 space-y-3 rounded-lg border p-4" role="status">
        {preview.selection.mode === "exceptUsers" && <p className="font-medium text-amber-700 dark:text-amber-300">{users.length.toLocaleString("fa-IR")} نفر از دریافت شارژ مستثنا هستند.</p>}
        <p>شارژ {preview.count.toLocaleString("fa-IR")} کاربر، هر نفر {preview.amount.toLocaleString("fa-IR")} تومان</p>
        <p>مبلغ کل: {(preview.count * preview.amount).toLocaleString("fa-IR")} تومان</p>
        <button type="button" disabled={busy} onClick={submit} className="rounded-lg bg-green-700 px-5 py-2.5 text-white disabled:opacity-50">تأیید و شارژ کیف پول‌ها</button>
      </div>}
    </AdminFormLayout>
  );
}
